import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Colors } from 'context';
import { careerType } from 'services/careerType';
import { CareerSkillStatus } from 'core/hooks/CareerSkillGraph';

import { REQUIREMENT_LABEL_KEY, RequirementDisplay } from '../model/skillLevelLabel';
import {
  EDGE_LABEL_KEY,
  SKILL_STATUS_LOOK,
  STATUS_TINT_DARK,
  STATUS_TINT_LIGHT,
  STATUS_TINT,
  tintVars,
} from '../skillStatus';

/**
 * Легенда карты (ТЗ 0070, статусы ТЗ 0071 п. 6.3).
 *
 * 🔴 Слова легенды — ТЕ ЖЕ, что вторая строка на карточке узла: «Spełnia
 * wymagania», «Do rozwinięcia», «W trakcie», «Do zdobycia». Прежняя легенда
 * перечисляла семь серверных статусов («Potwierdzona», «Brak — kluczowa»…),
 * а на узлах уже стояли слова требования, — и человек сверял карту со
 * справкой, в которой не было ни одного слова с карты.
 *
 * Образец рядом со словом — рамка и заливка того вида, которым такой узел
 * рисуется на холсте. «Do rozwinięcia» и «W trakcie» у сервера один статус
 * (`in_progress`), и образец у них честно общий: различает их вторая строка.
 * Ключевая и опциональная — отдельно: это важность, а не положение.
 */
const REQUIREMENT_ROWS: { display: RequirementDisplay; sample: CareerSkillStatus }[] = [
  { display: 'meets', sample: 'confirmed' },
  { display: 'improve', sample: 'in_progress' },
  { display: 'upcoming', sample: 'in_progress' },
  { display: 'missing', sample: 'missing' },
];

const IMPORTANCE_ROWS: { labelKey: string; sample: CareerSkillStatus }[] = [
  { labelKey: 'careerCenter.map.legend.key', sample: 'missing_critical' },
  { labelKey: 'careerCenter.map.legend.optional', sample: 'optional' },
];

const DASH: Record<string, string | undefined> = {
  solid: undefined,
  double: undefined,
  dashed: '5 3',
  dotted: '2 3',
};

const Sample = ({ status }: { status: CareerSkillStatus }) => {
  const look = SKILL_STATUS_LOOK[status];
  return (
    <svg width="28" height="16" viewBox="0 0 28 16" aria-hidden focusable="false">
      <rect
        x="1"
        y="1"
        width="26"
        height="14"
        rx="4"
        fill={STATUS_TINT[status]}
        stroke={look.accent}
        strokeWidth="2"
        strokeDasharray={DASH[look.border]}
      />
      {look.border === 'double' && (
        <rect x="4" y="4" width="20" height="8" rx="2" fill="none" stroke={look.accent} />
      )}
    </svg>
  );
};

export const SkillMapLegend = memo(({ capped }: { capped: boolean }) => {
  const { t } = useTranslation();

  return (
    <Root>
      {REQUIREMENT_ROWS.map((row) => (
        <Item key={row.display}>
          <Sample status={row.sample} />
          {t(REQUIREMENT_LABEL_KEY[row.display])}
        </Item>
      ))}
      {IMPORTANCE_ROWS.map((row) => (
        <Item key={row.labelKey}>
          <Sample status={row.sample} />
          {t(row.labelKey)}
        </Item>
      ))}
      <Note>{t(EDGE_LABEL_KEY.required)}</Note>
      <Note>{t(EDGE_LABEL_KEY.recommended)}</Note>
      {capped && <Warning>{t('careerCenter.map.tooManyEdges')}</Warning>}
    </Root>
  );
});

/** Образцы читают те же переменные заливки, что и холст, — объявлены здесь же. */
const Root = styled.div`
  ${tintVars(STATUS_TINT_LIGHT)}

  [data-theme='dark'] & {
    ${tintVars(STATUS_TINT_DARK)}
  }

  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  align-items: center;
`;

const Item = styled.span`
  ${careerType.meta()};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${Colors.black};
`;

const Note = styled.span`
  ${careerType.meta()};
  color: ${Colors.gray_dark};
`;

const Warning = styled.span`
  ${careerType.metaStrong()};
  color: ${Colors.signal_red};
`;
